import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  LinearProgress,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import api, { proverbService } from '../../services/api';

const Proverbs = () => {
  const [exportAnchor, setExportAnchor] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Telecharge tous les proverbes (actifs et inactifs) en Excel ou CSV
  const handleExport = async (format) => {
    setExportAnchor(null);
    setExporting(true);
    try {
      const res = await proverbService.exportFile(format);
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `proverbes-${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      window.alert("L'export a échoué. Réessayez dans un instant.");
    } finally {
      setExporting(false);
    }
  };

  const [proverbs, setProverbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedProverb, setSelectedProverb] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    duala_text: '',
    french_text: '',
    french_meaning: '',
    english_text: '',
    english_meaning: ''
  });
  const [error, setError] = useState('');

  const fetchProverbs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/proverbs', {
        params: {
          page: page + 1,
          limit: pageSize,
          search: search || undefined
        }
      });
      setProverbs(response.data.data);
      setTotal(response.data.pagination.total);
    } catch (err) {
      setError('Erreur lors du chargement des proverbes');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/proverbs/stats');
      setStats(response.data.data);
    } catch (err) {
      console.error('Erreur stats:', err);
    }
  };

  useEffect(() => {
    fetchProverbs();
    fetchStats();
  }, [page, pageSize, search]);

  const handleOpenDialog = (proverb = null) => {
    if (proverb) {
      setSelectedProverb(proverb);
      setFormData({
        title: proverb.title || '',
        duala_text: proverb.duala_text,
        french_text: proverb.french_text,
        french_meaning: proverb.french_meaning || '',
        english_text: proverb.english_text || '',
        english_meaning: proverb.english_meaning || ''
      });
    } else {
      setSelectedProverb(null);
      setFormData({
        title: '',
        duala_text: '',
        french_text: '',
        french_meaning: '',
        english_text: '',
        english_meaning: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedProverb(null);
  };

  const handleSubmit = async () => {
    try {
      if (selectedProverb) {
        await api.put(`/proverbs/${selectedProverb.id}`, formData);
      } else {
        await api.post('/proverbs', formData);
      }
      handleCloseDialog();
      fetchProverbs();
      fetchStats();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Voulez-vous vraiment désactiver ce proverbe ?')) {
      try {
        await api.delete(`/proverbs/${id}`);
        fetchProverbs();
        fetchStats();
      } catch (err) {
        setError('Erreur lors de la suppression');
      }
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setLoading(true);
      const response = await api.post('/proverbs/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert(`Import terminé: ${response.data.data.imported} proverbes importés`);
      fetchProverbs();
      fetchStats();
    } catch (err) {
      setError('Erreur lors de l\'import');
    } finally {
      setLoading(false);
      setImportDialogOpen(false);
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { 
      field: 'duala_text', 
      headerName: 'Proverbe (Duala)', 
      flex: 1,
      minWidth: 250
    },
    { 
      field: 'french_text', 
      headerName: 'Traduction (Français)', 
      flex: 1,
      minWidth: 250
    },
    { 
      field: 'french_meaning', 
      headerName: 'Sens', 
      flex: 1,
      minWidth: 200
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Modifier">
            <IconButton size="small" onClick={() => handleOpenDialog(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Désactiver">
            <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Proverbes Duala
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Statistiques */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Proverbes
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {stats.total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Utilisés en {stats.year}
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="primary">
                  {stats.usedThisYear}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Restants
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {stats.remaining}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Progression
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={parseFloat(stats.percentageUsed)} 
                    sx={{ flexGrow: 1, height: 10, borderRadius: 5 }}
                  />
                  <Typography variant="body2">{stats.percentageUsed}%</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Actions */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Rechercher (texte ou n° de proverbe)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 300 }}
          />
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            startIcon={exporting ? <CircularProgress size={18} /> : <DownloadIcon />}
            onClick={(e) => setExportAnchor(e.currentTarget)}
            disabled={exporting}
          >
            Exporter
          </Button>
          <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
            <MenuItem onClick={() => handleExport('xlsx')}>Excel (.xlsx)</MenuItem>
            <MenuItem onClick={() => handleExport('csv')}>CSV (.csv)</MenuItem>
          </Menu>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setImportDialogOpen(true)}
          >
            Importer Excel
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Ajouter
          </Button>
          <IconButton onClick={() => { fetchProverbs(); fetchStats(); }}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* Tableau */}
      <Paper sx={{ height: 600 }}>
        <DataGrid
          rows={proverbs}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={total}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          rowsPerPageOptions={[10, 20, 50]}
          disableSelectionOnClick
          localeText={{
            noRowsLabel: 'Aucun proverbe',
            MuiTablePagination: {
              labelRowsPerPage: 'Lignes par page'
            }
          }}
        />
      </Paper>

      {/* Dialog Ajout/Modification */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedProverb ? 'Modifier le proverbe' : 'Ajouter un proverbe'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Titre (optionnel)"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              fullWidth
            />
            <TextField
              label="Proverbe en Duala *"
              value={formData.duala_text}
              onChange={(e) => setFormData({ ...formData, duala_text: e.target.value })}
              fullWidth
              multiline
              rows={2}
              required
            />
            <TextField
              label="Traduction Française *"
              value={formData.french_text}
              onChange={(e) => setFormData({ ...formData, french_text: e.target.value })}
              fullWidth
              multiline
              rows={2}
              required
            />
            <TextField
              label="Sens (Français)"
              value={formData.french_meaning}
              onChange={(e) => setFormData({ ...formData, french_meaning: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Traduction Anglaise"
              value={formData.english_text}
              onChange={(e) => setFormData({ ...formData, english_text: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Sens (Anglais)"
              value={formData.english_meaning}
              onChange={(e) => setFormData({ ...formData, english_meaning: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!formData.duala_text || !formData.french_text}
          >
            {selectedProverb ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Import */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)}>
        <DialogTitle>Importer des proverbes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Sélectionnez un fichier Excel (.xls ou .xlsx) contenant les colonnes:
            DUALA, Français, Sens Français, Anglais, Sens Anglais
          </Typography>
          <input
            type="file"
            accept=".xls,.xlsx"
            onChange={handleImport}
            style={{ display: 'block', marginTop: 16 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Proverbs;
